import { Component, OnInit, ElementRef } from '@angular/core';
import {
  NavbarComponent,
  SelectedCustomerService,
  AuthService,
  CartService,
  NavbarConfigService,
  GungFlowService,
  BaseViewConfigService,
  GungFlow,
  GungModalService,
  ProductViewType
} from 'gung-standard';
import { getDefaultConfiguration } from '../../utilities/default-configurations';
import { takeUntil, switchMap, tap, filter, first, map } from 'rxjs';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment' 
import { GlobalSearchConfigService } from 'gung-standard';


export interface refRoute {
  heading: string;
  route?: string;
  action?(event?): void;
}

@Component({
    selector: 'ref-ref-navbar',
    templateUrl: './ref-navbar.component.html',
    styleUrls: ['./ref-navbar.component.scss'],
    standalone: false
})
export class RefNavbarComponent extends NavbarComponent implements OnInit {
  public showDropShadow: boolean;
  public logoSrc = getDefaultConfiguration().logoSrc;
  extraLinks: refRoute[];
  environment = environment;

  constructor(
    selectedCustomerService: SelectedCustomerService,
    authService: AuthService,
    cartService: CartService,
    elementRef: ElementRef,
    navbarConfigService: NavbarConfigService,
    protected gungFlowService: GungFlowService,
    protected router: Router,
    protected gungModalService: GungModalService,
    protected baseViewConfigService: BaseViewConfigService,
    protected globalSearchConfig: GlobalSearchConfigService
  ) {
    super(selectedCustomerService, authService, cartService, elementRef, navbarConfigService, gungFlowService, router, globalSearchConfig);
  }

  ngOnInit() {
    this.change
      .asObservable()
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap(() => {
          return this.authService.getIsAuthenticated().pipe(
            tap(authed => (this.signedIn = authed)),
            filter(authenticated => !!authenticated),
            tap(authenticated => (this.authenticated = authenticated)),
            switchMap(() =>
              forkJoin([
                this.authService.getRoles().pipe(
                  filter(roles => !!roles),
                  first()
                ),
                this.selectedCustomerService.getSelectedCustomer().pipe(
                  filter(customer => !!customer),
                  first()
                ),
                this.gungFlowService.getGungFlows().pipe(first())
              ])
            )
          );
        })
      )
      .subscribe(([roles, customer, gungflows]) => {
        let flowsMenu: refRoute[] = [];
        gungflows = gungflows.sort((a, b) =>
          a.extra.sortingPriority > b.extra.sortingPriority
            ? 1
            : b.extra.sortingPriority > a.extra.sortingPriority
            ? -1
            : 0
        );
        gungflows.map(flow => {
          flowsMenu.push({
            heading: flow.name,
            action: () => {
              this.selectFlow(flow.id);
              this.redirectToProducts();
            }
          }) as unknown as refRoute;
        });

        this.selectedCustomer = customer;
        if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
          this.isSales = true;
          this.extraLinks = this.getSalesMenu();
          this.extraLinks.push(...flowsMenu);
        } else {
          this.isSales = false;
          this.extraLinks = this.getCustomerMenu(customer.id);
          this.extraLinks.push(...flowsMenu)
        }
        this.extraLinks = this.extraLinks.length > 6 ? this.extraLinks.slice(0, 5) : this.extraLinks;
      });

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.change.next(true));

    this.cartService
      .getCurrentCart()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(cart => (this.cart = cart));

    this.isCollapsed = true;
    this.isCartVisible = false;
    this.isCustomerCollapsed = true;

    window.addEventListener('scroll', event => {
      this.showDropShadow = window.scrollY >= 240;
    });
  }

  selectFlow(flowId: string): void {
    forkJoin([this.gungFlowService.getSelectedFlow().pipe(first()), this.cartService.getCurrentCart().pipe(first())])
      .pipe(first())
      .subscribe(([flow, cart]) => {
        if (flow.id === flowId) {
          return;
        }
        if (cart.length === 0) {
          this.gungFlowService.selectFlow(flowId);
          return;
        }
        this.gungModalService
          .openConfirmYesNoModal('FLOW_CHANGE', 'FLOW_CHANGE_CLEAR_CART_CONFIRM', {}, 'OK', 'CANCEL')
          .then(
            result => {
              if (result) {
                this.gungFlowService.selectFlow(flowId);
              }
            },
            reason => undefined
          );
      });
  }

  redirectToProducts(flow?: GungFlow): void {
    this.router.navigate([this.getProductListBaseRoute()]);
  }

  getProductListBaseRoute() {
    if (this.baseViewConfigService.productViewType === ProductViewType.assortmentTreeView) {
      return '/articles';
    }
    return '/products';
  }

  protected getSalesMenu(): refRoute[] {
    const baseMenu: refRoute[] = [
      {
        route: '/customers',
        heading: 'MY_CUSTOMERS'
      },
      {
        route: '/open-orders',
        heading: 'OPEN_ORDERS'
      }
    ];
    return baseMenu;
  }

  protected getCustomerMenu(customerId: string): refRoute[] {
    const baseMenu: refRoute[] = [
      {
        route: `${'/customers/' + customerId}`,
        heading: 'MY_PAGES'
      },
      {
        route: '/orders',
        heading: 'PREVIOUS_ORDERS'
      }
    ];
    return baseMenu;
  }

  public toggleMobileMenu() {
    this.baseCollapseClasses.show = !this.baseCollapseClasses.show;
    this.isCollapsed = this.baseCollapseClasses.show;
  }
}
